import { useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";

/**
 * `editor.isEditable`, as state.
 *
 * Node views read it at render time, but nothing re-renders them when it
 * changes: `setEditable` reconfigures the view without touching the document,
 * so a node view keeps whatever chrome it last drew. That was invisible while
 * editable flipped once in an editor's life; `InlineEdit` now flips it on every
 * click into a field and every commit out of one, so the chrome has to follow —
 * a code block's language picker, an image's caption input.
 *
 * `setEditable` emits `update`, which is what this subscribes to.
 */
export function useIsEditable(editor: Editor): boolean {
  const [editable, setEditable] = useState(() => editor.isEditable);

  useEffect(() => {
    const sync = () => setEditable(editor.isEditable);
    // Once on mount too: the editor may have been reconfigured between the
    // initial state and this effect.
    sync();
    editor.on("update", sync);
    return () => {
      editor.off("update", sync);
    };
  }, [editor]);

  return editable;
}
